import { AmbientLight, Scene, Vector3 } from 'three';
import { CustomMouseEvent } from '../controls';
import { Viewer } from '../main';
import LocalScene from '../projections';
import {
  AerialAreaMeasurement,
  AerialLengthMeasurement,
  Measurement3D,
  MeasurementArea,
  MeasurementPoint,
  MinimapMeasure,
  MeasurementConstructor,
  MeasurementFormatter,
  MeasurementGroup,
  MeasurementGroupExporter,
  MeasurementSceneView,
  MeasureEndpoint,
  MeasurementVolume,
  HeightMeasurement,
  MeasureType,
  VolumeType,
  SamplingRate
} from '.';
import { clearAerialTooltip, clearViewerTooltip } from '../../viewer';
import { RayCaster } from '../ray-caster';
import { toast } from '../../../app';
import { t } from '../../../localization';
import { ChangeDetector } from '../utilities';

export class MeasurementController {
  public viewer: Viewer;
  public enabled = false;
  public timeout = false;
  private measureType = MeasureType.Length;
  public volumeType = VolumeType.MultiPlane;
  public sampleRate = SamplingRate.Medium;

  public formatter: MeasurementFormatter;
  public measurementSceneView: MeasurementSceneView;
  public measurementAerialView: MinimapMeasure;
  public measurementBuilder: MeasurementConstructor;
  public measurementGroupExporter: MeasurementGroupExporter;
  public measurementGroup: MeasurementGroup;
  public setMeasurements;
  public scene: Scene;

  constructor(viewer: Viewer, props) {
    const {setMeasurements} = props;

    this.viewer = viewer;
    this.setMeasurements = setMeasurements;

    this.initScene();

    this.formatter = new MeasurementFormatter("m");
    this.measurementSceneView = new MeasurementSceneView(this, this.scene);
    this.measurementAerialView = new MinimapMeasure(this);
    this.measurementBuilder = new MeasurementConstructor(this);
    this.measurementGroupExporter = new MeasurementGroupExporter(this);
    this.measurementGroup = new MeasurementGroup(this);
  }

  get pointclouds() {
    return this.viewer.pointclouds;
  }

  get controls() {
    return this.viewer.controls;
  }

  get isImageOnly() {
    return this.viewer.isImageOnly;
  }

  get measuring() {
    return this.measurementBuilder.active;
  }

  get activeMeasurement() {
    return this.measurementBuilder.measurement;
  }

  get notEmpty() {
    return this.measurementGroup.measurements.length > 0;
  }

  get dragObject() {
    return this.measurementSceneView.dragObject;
  }

  get editable() {
    // Always true since view-only mode doesnt exist
    return true;
  }

  get hoveredPoint() {
    return this.measurementSceneView.hoveredPoint;
  }

  setState(state) {
    if (state) {
      this.enable();
    } else {
      this.disable(true);
    }
  }

  enable() {
    this.enabled = true;
    this.createNewMeasurementGroup();
    this.measurementAerialView.enabled = true;
    this.measurementAerialView.init();
  }

  disable(clear=false) {
    if (clear) {
      this.measurementSceneView.removeAll();
      this.measurementAerialView.removeAll();
    }

    this.enabled = false;
    this.stopDrawingItems();
    this.measurementGroup.reset();
    this.measurementAerialView.enabled = false;
    this.measurementAerialView.removeInteraction();
    this.viewer.multiImageWindow.close();

    clearAerialTooltip();
    clearViewerTooltip();
  }

  initScene() {
    this.scene = new Scene();
    this.scene.add(new AmbientLight(0xFFFFFF));
  }

  enableTimeout() {
    this.timeout = true;
    setTimeout(() => {
      this.timeout = false;
    }, 500);
  }

  loadMeasurementData(data) {
    try {
      const projection = data.projection;
      const sameProjection = LocalScene.isDataProjection(projection.name);

      if (!sameProjection) {
        toast.error(t("toast.measure-projection-mismatch", {
          projection: projection.name
        }));

        return false;
      };

      // Clear data and create new group
      this.createNewMeasurementGroup();

      // Load measurements into new group
      let measurements = data.measurements;
      this.measurementGroup.loadMeasurements(measurements);
      this.refreshMeasurementList();

      toast.success(t("toast.measure-load-success", {
        count: data.count
      }));
    } catch {
      this.createNewMeasurementGroup();
      toast.error(t("toast.measure-load-error"));
    }
  }

  cancelMeasurement() {
    this.stopDrawingItems();
    this.viewer.multiImageWindow.close();
  }

  returnCoordinate(obj) {
    return false;
    /*
    return isLineworkObject(obj)
      || isFeatureServerObject(obj)
      || obj instanceof TexturedSprite;
    */
  }

  getCoordinate(event) : Vector3 {
    return this.controls.getPickerResults(event).coordinate;
  }

  getAreaGeometry() {
    const temporaryMeasurement = this.activeMeasurement;
    return this.measurementGroup
      .getAreaGeometry(temporaryMeasurement);
  }

  /** @private */
  clickMultiImage(event) {
    if (!this.editable) {
      return;
    }

    const measurement = this.measurementBuilder.measurement;
    const multiImage = this.viewer.multiImageWindow;
    const isSinglePoint = measurement instanceof MeasurementPoint;

    // Calculate clicked position
    const raycaster = new RayCaster(this.viewer, event);
    const direction = raycaster.vectorFromEvent(event);
    const position = this.viewer.camera.position;

    multiImage.show({
      closePrompt: () => {
        if (measurement.hasPoints) {
          return {
            title: t('asset.discard-measurements'),
            text: t('asset.active-measurements-found'),
            callback: () => {
              this.cancelMeasurement();
              clearViewerTooltip();
            }
          };
        }
      },
      onComplete: () => {
        multiImage.getClickedPoint(coordinate => {
          const isFinished = this.onClickFinish(coordinate);

          // Certain measurement types require a simulated double-click
          if (!isFinished) {
            this.onClickFinish(coordinate);
          }

          clearViewerTooltip();
          multiImage.close();
        });
      },
      onAddMore: () => {
        multiImage.getClickedPoint(coordinate => {
          this.onClickFinish(coordinate);
          multiImage.canComplete = measurement.canFinish;
          multiImage.resetObservations();
        });
      },
      canComplete: measurement.canFinish,
      canAddMore: !isSinglePoint,
    });

    multiImage.addFirstObservation(position, direction);
  }

  /** @private */
  clickStandard(event) {
    let coordinate = this.hoveredPoint
      ? this.hoveredPoint.position
      : this.getCoordinate(event);

    if (!coordinate) return;
    this.onClickFinish(coordinate);
  }

  /** @private */
  onClickFinish(coordinate) {
    let isFinished = false;
    let result = this.measurementBuilder.add(coordinate);

    if (!result.valid) {
      this.cancelMeasurement();
      return isFinished;
    }

    if (result.added) {
      this.measurementSceneView.addTemporaryPoint(coordinate);
      this.updateMeasureText();
    }

    if (this.measurementBuilder.isFinished) {
      isFinished = true;
      this.addSceneMeasurement();
    };

    return isFinished;
  }

  onMouseUp(event: CustomMouseEvent): boolean {
    if (event.mouseMoved || !this.enabled) {
      return false;
    }

    if (event.isLeftClick) {
      this.mouseClick(event);
      return true;
    }

    if (event.isRightClick) {
      if (this.measuring) {
        // Cancel measurement
        this.cancelMeasurement();
        return true;
      }
    }

    return false;
  }

  mouseClick(event) {
    if (!this.editable || this.timeout) return;

    if (this.isImageOnly) {
      // Image only multi-image measurements
      this.clickMultiImage(event);
    } else {
      this.clickStandard(event);
    }
  }

  onMouseMoveAerial(event) {
    return this.measurementAerialView.onMouseMove(event);
  }

  onMouseMoveScene(event: CustomMouseEvent) {
    return this.measurementSceneView.onMouseMove(event);
  }

  resetDragObject() {
    this.measurementSceneView.setDragObject(null);
  }

  addSceneMeasurement() {
    this.updateMeasureText();

    let measurement = this.measurementBuilder.getMeasurement();

    if (measurement instanceof MeasurementArea) {
      measurement.generateSurface();
    }

    if (measurement instanceof HeightMeasurement) {
      this.enableTimeout();
    }

    this.measurementSceneView.removeTemporaryObjects();
    this.measurementGroup.add(measurement);
    this.renderMeasurement(measurement);
    this.markLastPointDraggable(measurement);

    if (measurement instanceof MeasurementVolume) {
      measurement.calculateVolume();
    }
  }

  addAerialMeasurement(geometry, measurementType) {
    const measurement = measurementType === 'LineString'
      ? new AerialLengthMeasurement(geometry)
      : new AerialAreaMeasurement(geometry);

    measurement.setController(this);
    this.measurementGroup.add(measurement);
    this.renderMeasurement(measurement);
  }

  markLastPointDraggable(measurement) {
    let sceneEndpoints = this.scene.children
      .filter(x => x instanceof MeasureEndpoint) as MeasureEndpoint[];

    let measureEndpoints = sceneEndpoints
      .filter(x => x.measurementID === measurement.id);

    let lastPoint = measureEndpoints[measureEndpoints.length - 1];
    this.measurementSceneView.setDragObject(lastPoint);
  }

  stopDrawingItems() {
    this.measurementBuilder.reset();
    this.measurementSceneView.removeTemporaryObjects();
    this.measurementAerialView.reloadInteraction();
    this.updateMeasureText(false);
  }

  setPointGrabTooltip() {
    this.measurementSceneView.setPointGrabTooltip();
  }

  measureGrabFinished() {
    this.measurementSceneView.measureGrabFinished();
  }

  refreshMeasurementList() {
    this.setMeasurements([...this.measurementGroup.measurements]);
  }

  updateMeasureText(checkMorePoints = true) {
    if (!this.enabled) return false;

    let morepoints = false;
    let measurement = this.measurementBuilder.measurement;

    if (checkMorePoints) {
      morepoints = !this.measurementBuilder.isFinished;
      if (measurement.vertices.length === 0) {
        morepoints = false;
      }
    }

    this.measurementSceneView.setTooltipText(measurement, morepoints);
  }

  createNewMeasurementGroup() {
    this.measurementAerialView.removeAll();
    this.measurementSceneView.removeAll();
    this.measurementGroup = new MeasurementGroup(this);
    this.setMeasurementType(this.measureType);
  }

  setMeasurementType(measurementType: MeasureType) {
    if (!this.enabled) return;

    this.measureType = measurementType;
    this.measurementBuilder.setBuilder(measurementType);
    this.measurementSceneView.removeTemporaryObjects();
    this.viewer.multiImageWindow.close();

    let measurement = this.measurementBuilder.measurement;
    this.measurementSceneView.setTooltipText(measurement);

    this.stopDrawingItems();
    this.measurementAerialView.setMeasurementType(measurementType);
  }

  removeByID(id) {
    this.measurementSceneView.removeByID(id);
    this.measurementGroup.removeByID(id);
    this.measurementAerialView.removeByID(id);

    clearViewerTooltip();
  }

  setTitle(id, title) {
    const measurement = this.measurementGroup.getByID(id);

    if (measurement) {
      measurement.setTitle(title);
      this.refreshMeasurementList();
    }

    clearViewerTooltip();
  }

  removeAll() {
    this.measurementGroup.removeAll();
    this.clearViews();
  }

  clearViews() {
    this.measurementSceneView.removeAll();
    this.measurementAerialView.removeAll();
  }

  export(type, ordering) {
    if (type === "csv") {
      this.exportCSV(ordering);
    } else if (type === "dat") {
      this.exportDAT();
    }
  }

  exportDAT() {
    this.measurementGroupExporter
      .exportDAT(this.measurementGroup);
  }

  exportCSV(ordering) {
    this.measurementGroupExporter.exportCSV(
      this.measurementGroup, ordering);
  }

  /* Not implemented */
  exportLinework(type, projection, version) {
    this.measurementGroupExporter
      .exportLinework(this.measurementGroup, type, version);
  }

  setUnits(units) {
    this.formatter.setUnits(units);

    if (this.measurementGroup) {
      this.reload();
    }
  }

  setVolumeType(type: VolumeType) {
    this.volumeType = type;
    this.refreshVolume();
  }

  setSampleRate(rate: SamplingRate) {
    this.sampleRate = rate;
    this.refreshVolume();
  }

  reload() {
    this.clearViews();
    this.measurementGroup.measurements.forEach(measurement => {
      this.renderMeasurement(measurement);
    });
    this.refreshMeasurementList();
  }

  refreshVolume() {
    // All volumes will need to be recalculated
    this.measurementGroup.measurements.forEach(measurement => {
      if (measurement instanceof MeasurementVolume) {
        measurement.stopVolumeCalculation();
        measurement.calculateVolume();
      }
    });
  }

  setHover(measurement, hover = false) {
    if (measurement instanceof Measurement3D) {
      this.measurementSceneView.setHover(measurement.id, hover);
    } else {
      this.measurementAerialView.setHover(measurement.id, hover);
    }
  }

  renderMeasurement(measurement) {
    if (measurement instanceof Measurement3D) {
      this.measurementSceneView.render(measurement);
    } else {
      this.measurementAerialView.render(measurement);
    }
  }

  update(changeDetector: ChangeDetector) {
    this.measurementSceneView.update(changeDetector);
  }
}
